// app/components/chart/RealtimeCandlestickChart.tsx
"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  IChartApi,
  ISeriesApi,
  Time,
  MouseEventParams,
} from "lightweight-charts";

import { ChartContainer } from "./ChartContainer";
import { ChartControls } from "./ChartControls";
import { ChartFooter } from "./ChartFooter";
import { ChartIndicators } from "./ChartIndicators";
import { VectorCandleMarkers } from "./VectorCandleMarkers";
import { BreakoutSignalsIndicator } from "../indicators/BreakoutSignals";
import { TcTrendIndicator } from "../indicators/TcTrendIndicator";
import { FearGreedIndicator } from "../indicators/FearGreedIndicator";
import { AuctionMarketIndicator } from "../indicators/AuctionMarketIndicator";
import { AuctionMarketZones } from "../indicators/AuctionMarketZones";
import { AddTickerModal } from "../modals/AddTickerModal";
import { EditTickerModal } from "../modals/EditTickerModal";
import { DebugModal } from "../modals/DebugModal";
import { BacktestModal } from "../modals/BacktestModal";
import { AuthRequiredModal } from "../modals/AuthRequiredModal";
import { useMarketSelection } from "../../hooks/useMarketSelection";
import { useMarketStreams } from "../../hooks/useMarketStreams";
import { useMarketCatalog } from "../../hooks/useMarketCatalog";
import {
  WSTcTrendPayload,
  TcTrendSnapshot,
  WSDeltaTrendPayload,
  WSFearGreedPayload,
  WSAuctionMarketPayload,
  LineDTO,
} from "@lacon/ws";
import { useChartState } from "../../hooks/useChartState";
import { useSocket } from "../../hooks/useSocket";
import { fetchFromBackend } from "../../lib/utils/api";
import { formatTime } from "../../lib/utils/formatTime";
import { Timeframe } from "../../lib/enums/timeframe.enum";
import { EventTypes } from "../../lib/enums/event-types.enum";
import { DEFAULT_CANDLE_SERIES_CONFIG } from "../../lib/constants/chart.constants";
import { CandleDto } from "../../lib/interface/CandleDto.interface";
import { InitialData } from "../../lib/interface/InitialData.interface";
import { VectorCandleEvent } from "../../lib/interface/VectorCandleEvent.interface";
import { BreakoutSignal } from "../../lib/interface/BreakoutSignal.interface";
import { FearCarverSignal } from "../../lib/interface/FearCarverSignal.interface";
import { DeltaTrendIndicator } from "../indicators/DeltaTrendIndicator";
import { useUserRole } from "@/hooks/useUserRole";
import { useIndicators } from "@/providers/IndicatorsProvider";
import { ActionButton } from "../buttons/ActionButton";
import { IndicatorSelector } from "../selectors/IndicatorSelector";
import { debugLog, debugWarn } from "@/lib/utils/debug";
import {
  buildPairAvailability,
  isTimeframe,
  sortTimeframes,
  TickerWithPairs,
} from "./chart-availability";

// Pair availability helpers extracted into `chart-availability.ts`.

export const RealtimeCandlestickChart: React.FC = () => {
  // Custom hooks
  const { socketRef, isConnected } = useSocket();
  const { isSignedIn } = useUserRole();
  const {
    pairs,
    timeframes,
    exchanges,
    selectedPair,
    selectedTimeframe,
    selectedExchange,
    setSelectedPair,
    setSelectedTimeframe,
    setSelectedExchange,
    initializeSelection,
  } = useMarketSelection();

  const {
    candles,
    plasmaLines,
    supLines,
    resLines,
    vectorCandles,
    clearVectorCandles,
    tcTrend,
    tcTrendChanges,
    clearTcTrend,
    deltaTrend,
    fearGreed,
    auctionMarket,
    updateCandles,
    updateSingleCandle,
    updateVectorCandle,
    updatePlasmaLines,
    updateSupResLines,
    updateTcTrend,
    updateDeltaTrend,
    updateFearGreed,
    clearTcTrendChanges,
    clearFearGreed,
    updateAuctionMarket,
    clearAuctionMarket,
  } = useMarketStreams();

  const { pairAvailability, setPairAvailability, allTickers, setAllTickers } =
    useMarketCatalog();

  const {
    chartState,
    isDebugVisible,
    isAddTickerVisible,
    isEditTickerVisible,
    error,
    setChartState,
    setError,
    toggleDebugModal,
    toggleAddTickerModal,
    toggleEditTickerModal,
  } = useChartState();
  const {
    enabledStates,
    toggle,
    isEnabled,
    getEnabled,
    activeIndicatorIds,
    backtestableIndicatorIds,
    backtestIndicators,
    setActiveIndicators,
  } = useIndicators();

  const showPlasma = isEnabled("plasma");
  const showSupportResistance = isEnabled("supportresistance");
  const showBreakouts = isEnabled("breakout");
  const showVectorCandles = isEnabled("vectorcandles");
  const showTcTrend = isEnabled("tctrend");
  const showFearCarver = isEnabled("fearcarver");
  const showTcBreakout = isEnabled("tcbreakout");
  const showAuctionMarket = isEnabled("auctionmarket");

  const handleInitialData = useCallback(
    (data: InitialData) => {
      initializeSelection(data);
      setActiveIndicators(data.activeIndicators ?? []);
    },
    [initializeSelection, setActiveIndicators]
  );

  // Do not auto-enable indicators; backend-active only controls availability.

  // Track layout breakpoint for header/footer sizing
  const [isMobileLayout, setIsMobileLayout] = useState(false);

  // Add state for storing breakout signals
  const [breakoutSignals, setBreakoutSignals] = useState<BreakoutSignal[]>([]);
  const [fearCarverSignals, setFearCarverSignals] = useState<
    FearCarverSignal[]
  >([]);
  const [tcBreakoutSignals, setTcBreakoutSignals] = useState<BreakoutSignal[]>(
    []
  );
  const [hoveredFearCarver, setHoveredFearCarver] =
    useState<FearCarverSignal | null>(null);
  const [hoveredTcBreakout, setHoveredTcBreakout] =
    useState<BreakoutSignal | null>(null);

  const [noDataAvailable, setNoDataAvailable] = useState(false);
  const [isBacktestOpen, setBacktestOpen] = useState(false);
  const [isAuthModalVisible, setAuthModalVisible] = useState(false);
  const lastTimeRef = React.useRef<number | null>(null);
  const getExchangesForPair = useCallback(
    (pair: string) => {
      const exchangesForPair = pairAvailability[pair];
      if (exchangesForPair) {
        return Object.keys(exchangesForPair);
      }
      return exchanges;
    },
    [pairAvailability, exchanges]
  );

  const getTimeframesForPair = useCallback(
    (pair: string, exchange?: string) => {
      const availability = pairAvailability[pair];
      if (availability) {
        const forExchange = exchange ? availability[exchange] : undefined;
        if (forExchange?.length) {
          return forExchange;
        }
        const union = Object.values(availability).flat();
        if (union.length) {
          return sortTimeframes(union);
        }
        return [];
      }

      return sortTimeframes(timeframes.map((tf) => tf as Timeframe));
    },
    [pairAvailability, timeframes]
  );

  const normalizeSelection = useCallback(
    (next: { pair?: string; exchange?: string; timeframe?: Timeframe }) => {
      const targetPair = next.pair ?? selectedPair;
      const exchangeChoices = targetPair
        ? getExchangesForPair(targetPair)
        : exchanges;
      const desiredExchange = next.exchange ?? selectedExchange;
      const resolvedExchange =
        desiredExchange && exchangeChoices.includes(desiredExchange)
          ? desiredExchange
          : (exchangeChoices[0] ?? desiredExchange ?? "");

      const timeframeChoices = targetPair
        ? getTimeframesForPair(targetPair, resolvedExchange)
        : sortTimeframes(timeframes.filter(isTimeframe));
      const desiredTimeframe = next.timeframe ?? selectedTimeframe;
      const resolvedTimeframe =
        timeframeChoices.find((tf) => tf === desiredTimeframe) ??
        timeframeChoices[0] ??
        desiredTimeframe;

      return {
        pair: targetPair,
        exchange: resolvedExchange,
        timeframe: resolvedTimeframe,
      };
    },
    [
      exchanges,
      getExchangesForPair,
      getTimeframesForPair,
      selectedExchange,
      selectedPair,
      selectedTimeframe,
      timeframes,
    ]
  );

  // Normalize candlestick data: unique times (keep last), sorted by time asc
  const normalizeCandleData = useCallback(
    (
      items: Array<{
        time: Time;
        open: number;
        high: number;
        low: number;
        close: number;
        color?: string;
        borderColor?: string;
        wickColor?: string;
      }>
    ) => {
      const map = new Map<number, (typeof items)[number]>();
      for (const it of items) {
        const t = it.time as number;
        if (!isFinite(t)) continue;
        map.set(t, it);
      }
      return Array.from(map.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, v]) => v);
    },
    []
  );

  // Re-apply cached candles when the chart initializes or when cached candles change
  useEffect(() => {
    if (!chartState.candleSeries || candles.length === 0) return;
    const candleData = candles
      .map((candle) => {
        const t = formatTime(candle.timestamp);
        const o = Number(candle.open);
        const h = Number(candle.high);
        const l = Number(candle.low);
        const c = Number(candle.close);
        if (
          !isFinite(t) ||
          !isFinite(o) ||
          !isFinite(h) ||
          !isFinite(l) ||
          !isFinite(c)
        ) {
          return null;
        }
        return {
          time: t as Time,
          open: o,
          high: h,
          low: l,
          close: c,
        };
      })
      .filter(
        (
          x
        ): x is {
          time: Time;
          open: number;
          high: number;
          low: number;
          close: number;
          color?: string;
          borderColor?: string;
          wickColor?: string;
        } => x !== null
      )
      .sort((a, b) => (a.time as number) - (b.time as number));

    const data = normalizeCandleData(candleData);
    chartState.candleSeries.setData(data);
    const last = data[data.length - 1]?.time as unknown as number | undefined;
    if (last !== undefined) lastTimeRef.current = last;
  }, [chartState.candleSeries, candles, normalizeCandleData]);

  // No per-candle color overrides; vectors are visualized via markers only.

  const handleToggleIndicatorUI = useCallback(
    (id: string, enabled: boolean) => {
      toggle(id, enabled);
    },
    [toggle]
  );

  // Update the handleBreakoutEvent to store signals
  const handleBreakoutEvent = useCallback(
    (signals: BreakoutSignal[]) => {
      setBreakoutSignals(signals); // Store the signals

      if (
        chartState.candleSeries &&
        chartState.chartInstance &&
        showBreakouts
      ) {
        const markers = signals.map((signal) => {
          const action = signal.action ?? signal.signal;
          return {
            time: formatTime(signal.timestamp),
            position:
              action === "LONG" ? ("belowBar" as const) : ("aboveBar" as const),
            color: action === "LONG" ? "#66bbff" : "#ff66bb",
            shape:
              action === "LONG" ? ("arrowUp" as const) : ("arrowDown" as const),
            text: action === "LONG" ? "BUY" : "SELL",
          };
        });

        // Marker rendering handled elsewhere; keep computed markers for future integration.
        debugLog(
          "Breakout signals received:",
          signals.length,
          "markers:",
          markers.length
        );
      }
    },
    [showBreakouts, chartState.candleSeries, chartState.chartInstance]
  );

  const handleTcBreakoutEvent = useCallback((signals: BreakoutSignal[]) => {
    setTcBreakoutSignals(signals);
  }, []);

  const handleFearCarverEvent = useCallback((signals: FearCarverSignal[]) => {
    setFearCarverSignals(signals);
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const handleMedia = () => setIsMobileLayout(media.matches);
    handleMedia();
    media.addEventListener("change", handleMedia);
    return () => media.removeEventListener("change", handleMedia);
  }, []);

  const handleCandleEvent = useCallback(
    (newCandle: CandleDto) => {
      if (!newCandle) {
        return;
      }

      setNoDataAvailable(false);
      updateSingleCandle(newCandle);

      if (chartState.candleSeries) {
        const t = formatTime(newCandle.timestamp);
        const o = Number(newCandle.open);
        const h = Number(newCandle.high);
        const l = Number(newCandle.low);
        const c = Number(newCandle.close);
        if (
          !isFinite(t) ||
          !isFinite(o) ||
          !isFinite(h) ||
          !isFinite(l) ||
          !isFinite(c)
        ) {
          return;
        }
        // Guard against out-of-order tick relative to last applied time
        const last = lastTimeRef.current ?? -Infinity;
        if (t < last) {
          return;
        }
        const candleData = {
          time: t as Time,
          open: o,
          high: h,
          low: l,
          close: c,
        };
        try {
          chartState.candleSeries.update(candleData);
          if (t >= last) lastTimeRef.current = t;
        } catch (err) {
          // Guard against transient chart updates during indicator toggles
          debugWarn("Skipping candle update due to chart state:", err);
        }
      }
    },
    [updateSingleCandle, chartState.candleSeries]
  );

  // Grey-out candles when Vector Candles are enabled; restore defaults when disabled
  useEffect(() => {
    if (!chartState.candleSeries) return;
    if (showVectorCandles) {
      try {
        chartState.candleSeries.applyOptions({
          upColor: "#6b7280",
          downColor: "#6b7280",
          borderUpColor: "#4b5563",
          borderDownColor: "#4b5563",
          wickUpColor: "#4b5563",
          wickDownColor: "#4b5563",
        });
      } catch {}
    } else {
      try {
        chartState.candleSeries.applyOptions({
          upColor: DEFAULT_CANDLE_SERIES_CONFIG.upColor,
          downColor: DEFAULT_CANDLE_SERIES_CONFIG.downColor,
          borderUpColor: DEFAULT_CANDLE_SERIES_CONFIG.borderUpColor,
          borderDownColor: DEFAULT_CANDLE_SERIES_CONFIG.borderDownColor,
          wickUpColor: DEFAULT_CANDLE_SERIES_CONFIG.wickUpColor,
          wickDownColor: DEFAULT_CANDLE_SERIES_CONFIG.wickDownColor,
        });
      } catch {}
    }
  }, [chartState.candleSeries, showVectorCandles]);

  const handleCandlesEvent = useCallback(
    (newCandles: CandleDto[]) => {
      if (!newCandles || newCandles.length === 0) {
        setNoDataAvailable(true);
        // Clear existing chart data
        if (chartState.candleSeries) {
          chartState.candleSeries.setData([]);
        }
        updateCandles([]);
        updatePlasmaLines([]);
        updateSupResLines([], []);
        return;
      }

      setNoDataAvailable(false);
      updateCandles(newCandles);

      debugLog(
        "Sample candle timestamps:",
        newCandles.slice(0, 3).map((c) => ({
          original: c.timestamp,
          formatted: formatTime(c.timestamp),
        }))
      );

      if (chartState.candleSeries) {
        const candleData = newCandles
          .map((candle) => {
            const t = formatTime(candle.timestamp);
            const o = Number(candle.open);
            const h = Number(candle.high);
            const l = Number(candle.low);
            const c = Number(candle.close);
            if (
              !isFinite(t) ||
              !isFinite(o) ||
              !isFinite(h) ||
              !isFinite(l) ||
              !isFinite(c)
            ) {
              return null;
            }
            return {
              time: t as Time,
              open: o,
              high: h,
              low: l,
              close: c,
            };
          })
          .filter(
            (
              x
            ): x is {
              time: Time;
              open: number;
              high: number;
              low: number;
              close: number;
              color?: string;
              borderColor?: string;
              wickColor?: string;
            } => x !== null
          )
          .sort((a, b) => (a.time as number) - (b.time as number));
        try {
          const data = normalizeCandleData(candleData);
          chartState.candleSeries.setData(data);
          const last = data[data.length - 1]?.time as unknown as
            | number
            | undefined;
          if (last !== undefined) lastTimeRef.current = last;
        } catch (e) {
          debugWarn("Failed to set candle series data:", e);
        }
      }
    },
    [
      updateCandles,
      chartState.candleSeries,
      updatePlasmaLines,
      updateSupResLines,
      normalizeCandleData,
    ]
  );

  const updateChartMarkers = useCallback(() => {
    // This function is now handled by the VectorCandleMarkers component
    // Keeping it for compatibility but removing the logging
  }, []);

  const handleVectorCandleEvent = useCallback(
    (vectorCandleData: VectorCandleEvent | VectorCandleEvent[]) => {
      // Handle both single events and arrays of events
      const vectorCandles = Array.isArray(vectorCandleData)
        ? vectorCandleData
        : [vectorCandleData];

      debugLog("Vector candle event received:", vectorCandles.length);

      vectorCandles.forEach((vectorCandle) => {
        const action = vectorCandle?.action ?? vectorCandle?.signal;
        if (!vectorCandle || action === "NONE") {
          return;
        }

        updateVectorCandle(vectorCandle);
      });

      updateChartMarkers();
    },
    [updateVectorCandle, updateChartMarkers]
  );

  // TcTrend snapshot handler
  const handleTcTrendEvent = useCallback(
    (payload: WSTcTrendPayload) => {
      if (!payload) return;
      // Ensure chronological order and feed into state to build change list
      const arr: TcTrendSnapshot[] = Array.isArray(payload)
        ? payload
        : [payload];
      arr
        .slice()
        .sort((a, b) => a.updatedAt - b.updatedAt)
        .forEach((snap) => updateTcTrend(snap));
    },
    [updateTcTrend]
  );

  // DeltaTrend handler
  const handleDeltaTrendEvent = useCallback(
    (payload: WSDeltaTrendPayload) => {
      updateDeltaTrend(payload);
    },
    [updateDeltaTrend]
  );

  const handleFearGreedEvent = useCallback(
    (payload: WSFearGreedPayload) => {
      updateFearGreed(payload);
    },
    [updateFearGreed]
  );

  const handleAuctionMarketEvent = useCallback(
    (payload: WSAuctionMarketPayload) => {
      const list = Array.isArray(payload) ? payload : [];
      updateAuctionMarket(list);
    },
    [updateAuctionMarket]
  );

  // Update markers when vectorCandles change
  useEffect(() => {
    updateChartMarkers();
  }, [updateChartMarkers]);

  // No vector recolor pass; markers visualize vector events.

  const handleChartReady = useCallback(
    (chart: IChartApi, candleSeries: ISeriesApi<"Candlestick">) => {
      setChartState({
        chartInstance: chart,
        candleSeries,
        resizeObserver: null,
      });
    },
    [setChartState]
  );

  useEffect(() => {
    return () => {
      setChartState({
        chartInstance: null,
        candleSeries: null,
        resizeObserver: null,
      });
    };
  }, [setChartState]);

  const lastViewRequestRef = React.useRef<string>("");

  useEffect(() => {
    if (!isConnected || !socketRef.current) return;
    if (!selectedPair || !selectedExchange || !selectedTimeframe) return;

    const viewKey = `${selectedPair}|${selectedExchange}|${selectedTimeframe}`;
    if (lastViewRequestRef.current === viewKey && candles.length > 0) {
      return;
    }

    socketRef.current.emit("changeView", [
      selectedTimeframe,
      selectedPair,
      selectedExchange,
    ]);
    lastViewRequestRef.current = viewKey;
  }, [
    isConnected,
    selectedPair,
    selectedExchange,
    selectedTimeframe,
    candles.length,
    socketRef,
  ]);

  // API handlers
  const handleActivatePair = async (id: number): Promise<boolean> => {
    try {
      await fetchFromBackend(`/pair/activate/${id}`, { method: "POST" });
      return true;
    } catch {
      setError("Failed to activate pair");
      return false;
    }
  };

  const handleDeactivatePair = async (id: number): Promise<boolean> => {
    try {
      await fetchFromBackend(`/pair/deactivate/${id}`, { method: "POST" });
      return true;
    } catch {
      setError("Failed to deactivate pair");
      return false;
    }
  };

  const handleViewChange = useCallback(
    (newPair?: string, newTimeframe?: Timeframe, newExchange?: string) => {
      if (socketRef.current) {
        // Clear existing data before requesting new data
        if (chartState.candleSeries) {
          chartState.candleSeries.setData([]);
        }
        updatePlasmaLines([]);
        updateSupResLines([], []);
        clearVectorCandles();
        clearTcTrend();
        clearTcTrendChanges();
        clearFearGreed();
        clearAuctionMarket();
        setBreakoutSignals([]);
        setFearCarverSignals([]);
        setTcBreakoutSignals([]);
        setNoDataAvailable(false);

        socketRef.current.emit("changeView", [
          newTimeframe || selectedTimeframe,
          newPair || selectedPair,
          newExchange || selectedExchange,
        ]);
      }
    },
    [
      selectedTimeframe,
      selectedPair,
      selectedExchange,
      chartState.candleSeries,
      updatePlasmaLines,
      updateSupResLines,
      clearVectorCandles,
      clearTcTrend,
      clearTcTrendChanges,
      clearFearGreed,
      clearAuctionMarket,
      socketRef,
    ]
  );

  const handlePairChange = useCallback(
    (pair: string) => {
      setSelectedPair(pair);
      handleViewChange(pair);
    },
    [setSelectedPair, handleViewChange]
  );

  const handleTimeframeChange = useCallback(
    (timeframe: string) => {
      const newTimeframe = timeframe as Timeframe;
      setSelectedTimeframe(newTimeframe);
      handleViewChange(undefined, newTimeframe);
    },
    [setSelectedTimeframe, handleViewChange]
  );

  const handleExchangeChange = useCallback(
    (exchange: string) => {
      const normalized = normalizeSelection({ exchange });
      setSelectedExchange(normalized.exchange);
      setSelectedTimeframe(normalized.timeframe);
      handleViewChange(
        normalized.pair,
        normalized.timeframe,
        normalized.exchange
      );
    },
    [
      handleViewChange,
      normalizeSelection,
      setSelectedExchange,
      setSelectedTimeframe,
    ]
  );

  useEffect(() => {
    if (!selectedPair) return;
    const normalized = normalizeSelection({});
    const needsExchangeUpdate =
      normalized.exchange && normalized.exchange !== selectedExchange;
    const needsTimeframeUpdate =
      normalized.timeframe && normalized.timeframe !== selectedTimeframe;

    if (needsExchangeUpdate || needsTimeframeUpdate) {
      if (needsExchangeUpdate) {
        setSelectedExchange(normalized.exchange);
      }
      if (needsTimeframeUpdate) {
        setSelectedTimeframe(normalized.timeframe);
      }
      handleViewChange(
        normalized.pair,
        normalized.timeframe,
        normalized.exchange
      );
    }
  }, [
    handleViewChange,
    normalizeSelection,
    selectedExchange,
    selectedPair,
    selectedTimeframe,
    setSelectedExchange,
    setSelectedTimeframe,
  ]);

  const handleAddTicker = async (tickerData: {
    symbol: string;
    pair: string;
    exchange: string;
    candleSyncTimeframes: Timeframe[];
  }): Promise<boolean> => {
    try {
      await fetchFromBackend("/ticker/create", {
        method: "POST",
        body: JSON.stringify(tickerData),
      });
      await fetchAllTickers({ surfaceError: false });
      return true;
    } catch {
      setError("Failed to add ticker");
      return false;
    }
  };

  const tickerLoadFailures = React.useRef(0);

  const openDebugModal = useCallback(
    () => toggleDebugModal(true),
    [toggleDebugModal]
  );
  const handleBacktest = useCallback(() => {
    if (!isSignedIn) {
      setAuthModalVisible(true);
      return;
    }
    setBacktestOpen(true);
  }, [isSignedIn]);

  const fetchAllTickers = useCallback(
    async (opts?: { surfaceError?: boolean }) => {
      try {
        const data = await fetchFromBackend<unknown>("/ticker/get");
        const tickerData: TickerWithPairs[] = Array.isArray(data)
          ? data.flatMap((item) => {
              if (!item || typeof item !== "object") {
                return [];
              }
              const record = item as Record<string, unknown>;
              if (typeof record.symbol !== "string") {
                return [];
              }

              const pairs = Array.isArray(record.pairs)
                ? record.pairs.flatMap((p) => {
                    if (!p || typeof p !== "object") return [];
                    const entry = p as Record<string, unknown>;
                    const pair = entry.pair;
                    const exchange = entry.exchange;
                    const timeframe = entry.timeframe;
                    if (
                      typeof pair === "string" &&
                      typeof exchange === "string" &&
                      isTimeframe(timeframe)
                    ) {
                      return [
                        {
                          pair,
                          exchange,
                          timeframe,
                          active:
                            typeof entry.active === "boolean"
                              ? entry.active
                              : undefined,
                        },
                      ];
                    }
                    return [];
                  })
                : undefined;

              return [{ symbol: record.symbol, pairs }];
            })
          : [];
        setAllTickers(tickerData.map((ticker) => ticker.symbol));
        setPairAvailability(buildPairAvailability(tickerData));
        tickerLoadFailures.current = 0;
        if (opts?.surfaceError) {
          setError(null);
        }
      } catch (error) {
        tickerLoadFailures.current += 1;

        // Only surface the error if we fail more than once; avoids flashing when a
        // retry succeeds shortly after the first attempt.
        if (opts?.surfaceError && tickerLoadFailures.current > 1) {
          setError("Failed to fetch tickers");
        } else {
          debugWarn("Failed to fetch tickers, will retry", error);
        }
      }
    },
    [setAllTickers, setError]
  );

  useEffect(() => {
    void fetchAllTickers({ surfaceError: false });
  }, [fetchAllTickers]);
  // Backtest indicators arrive via initial data and stay in local state.

  useEffect(() => {
    const chart = chartState.chartInstance;
    if (!chart) return;

    const handler = (param: MouseEventParams<Time>) => {
      const crosshairTime = param.time
        ? typeof param.time === "number"
          ? param.time
          : Number(param.time)
        : null;

      if (!crosshairTime || !showFearCarver) {
        setHoveredFearCarver(null);
      } else {
        const match = fearCarverSignals.find(
          (signal) => formatTime(signal.timestamp) === crosshairTime
        );
        setHoveredFearCarver(match ?? null);
      }

      if (!crosshairTime || !showTcBreakout) {
        setHoveredTcBreakout(null);
      } else {
        const match = tcBreakoutSignals.find(
          (signal) => formatTime(signal.timestamp) === crosshairTime
        );
        setHoveredTcBreakout(match ?? null);
      }
    };

    chart.subscribeCrosshairMove(handler);
    return () => {
      chart.unsubscribeCrosshairMove(handler);
    };
  }, [
    chartState.chartInstance,
    fearCarverSignals,
    tcBreakoutSignals,
    showFearCarver,
    showTcBreakout,
  ]);

  // Refresh tickers when opening Edit modal
  useEffect(() => {
    if (isEditTickerVisible) {
      void fetchAllTickers({ surfaceError: true });
    }
  }, [fetchAllTickers, isEditTickerVisible]);

  // Socket setup - re-attach listeners when socket connects/reconnects
  useEffect(() => {
    const socket = socketRef.current;
    if (socket && isConnected) {
      socket.on(EventTypes.InitialData, handleInitialData);
      socket.on(EventTypes.Candle, handleCandleEvent);
      socket.on(EventTypes.Candles, handleCandlesEvent);
      socket.on(EventTypes.VectorCandle, handleVectorCandleEvent);
      socket.on(EventTypes.PlasmaLines, updatePlasmaLines);
      socket.on(
        EventTypes.SupportResistance,
        (levels: { supportLines: LineDTO[]; resistanceLines: LineDTO[] }) => {
          if (!levels) return;
          updateSupResLines(
            levels.supportLines || [],
            levels.resistanceLines || []
          );
        }
      );
      socket.on(EventTypes.BreakoutCarver, handleBreakoutEvent);
      socket.on(EventTypes.TcBreakout, handleTcBreakoutEvent);
      socket.on(EventTypes.FearCarver, handleFearCarverEvent);
      socket.on(EventTypes.TcTrend, handleTcTrendEvent);
      socket.on(EventTypes.DeltaTrend, handleDeltaTrendEvent);
      socket.on(EventTypes.FearGreed, handleFearGreedEvent);
      socket.on(EventTypes.AuctionMarket, handleAuctionMarketEvent);

      // Re-assert our current view first (important for reconnects)
      if (selectedPair && selectedExchange) {
        socket.emit("changeView", [
          selectedTimeframe,
          selectedPair,
          selectedExchange,
        ]);
      }

      // Request initial data AFTER listeners are attached to avoid race condition
      socket.emit("getInitialData");

      return () => {
        socket.off(EventTypes.InitialData);
        socket.off(EventTypes.Candle);
        socket.off(EventTypes.Candles);
        socket.off(EventTypes.VectorCandle);
        socket.off(EventTypes.PlasmaLines);
        socket.off(EventTypes.SupportResistance);
        socket.off(EventTypes.BreakoutCarver);
        socket.off(EventTypes.TcBreakout);
        socket.off(EventTypes.FearCarver);
        socket.off(EventTypes.TcTrend);
        socket.off(EventTypes.DeltaTrend);
        socket.off(EventTypes.FearGreed);
        socket.off(EventTypes.AuctionMarket);
      };
    }
  }, [
    isConnected,
    handleInitialData,
    handleCandleEvent,
    handleCandlesEvent,
    handleVectorCandleEvent,
    handleBreakoutEvent,
    handleTcBreakoutEvent,
    handleFearCarverEvent,
    handleTcTrendEvent,
    handleDeltaTrendEvent,
    handleFearGreedEvent,
    handleAuctionMarketEvent,
    updatePlasmaLines,
    updateSupResLines,
    selectedExchange,
    selectedPair,
    selectedTimeframe,
    socketRef,
  ]);

  const pairOptions = useMemo(() => {
    const availablePairs = Object.keys(pairAvailability);
    if (availablePairs.length === 0) {
      return pairs;
    }
    const fromSocket = pairs.filter((pair) => availablePairs.includes(pair));
    const extras = availablePairs.filter((pair) => !fromSocket.includes(pair));
    return [...fromSocket, ...extras];
  }, [pairAvailability, pairs]);

  const exchangeOptions = useMemo(() => {
    if (selectedPair) {
      const options = getExchangesForPair(selectedPair);
      if (options.length) return options;
    }
    return exchanges;
  }, [exchanges, getExchangesForPair, selectedPair]);

  const timeframeOptions = useMemo(() => {
    if (selectedPair) {
      const options = getTimeframesForPair(selectedPair, selectedExchange);
      if (options.length) return options;
    }
    return sortTimeframes(timeframes.map((tf) => tf as Timeframe));
  }, [getTimeframesForPair, selectedExchange, selectedPair, timeframes]);

  const showExchangeSelect = exchangeOptions.length !== 1;

  const HEADER_HEIGHT_DESKTOP = 72;
  const HEADER_HEIGHT_MOBILE = 56;
  const FOOTER_HEIGHT_DESKTOP = 72;
  const headerHeight = isMobileLayout
    ? HEADER_HEIGHT_MOBILE
    : HEADER_HEIGHT_DESKTOP;
  const footerHeight = isMobileLayout ? 0 : FOOTER_HEIGHT_DESKTOP;

  return (
    <div
      className="relative w-full h-full mx-0 bg-slate-800 shadow-lg rounded-lg overflow-visible"
      style={{ paddingTop: `${headerHeight}px` }}
    >
      <ChartControls
        selectedPair={selectedPair}
        selectedTimeframe={selectedTimeframe}
        selectedExchange={selectedExchange}
        pairs={pairOptions}
        timeframes={timeframeOptions}
        exchanges={exchangeOptions}
        onPairChange={handlePairChange}
        onTimeframeChange={handleTimeframeChange}
        onExchangeChange={handleExchangeChange}
        onToggleIndicator={handleToggleIndicatorUI}
        indicatorEnabledStates={enabledStates}
        allowedIndicatorIds={activeIndicatorIds}
        onDebugClick={openDebugModal}
        onBacktestClick={handleBacktest}
      />

      <div className="relative p-2 bg-slate-850">
        <ChartContainer
          onChartReady={handleChartReady}
          headerOffset={headerHeight}
          footerOffset={footerHeight}
        />

        {/* No Data Overlay */}
        {noDataAvailable && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-800 bg-opacity-90">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-300 mb-2">
                No Data Available
              </h3>
              <p className="text-gray-400">
                No candle data found for {selectedPair} on {selectedTimeframe}
              </p>
            </div>
          </div>
        )}

        {chartState.chartInstance && (
          <ChartIndicators
            chart={chartState.chartInstance}
            plasmaLines={plasmaLines}
            supLines={supLines}
            resLines={resLines}
            showPlasma={showPlasma}
            showSupportResistance={showSupportResistance}
            formatTime={formatTime}
          />
        )}

        {/* Vector Candle Markers */}
        <VectorCandleMarkers
          vectorCandles={vectorCandles}
          chartInstance={chartState.chartInstance}
          candleSeries={chartState.candleSeries}
          showVectorCandles={showVectorCandles}
          deltaTrend={deltaTrend}
          showDeltaTrend={isEnabled("deltatrend")}
        />

        {/* DeltaTrend */}
        <DeltaTrendIndicator
          chartInstance={chartState.chartInstance}
          candleSeries={chartState.candleSeries}
          isEnabled={isEnabled("deltatrend")}
          deltaTrend={deltaTrend}
          formatTime={formatTime}
        />

        {/* Fear & Greed sentiment */}
        <FearGreedIndicator
          chartInstance={chartState.chartInstance}
          candleSeries={chartState.candleSeries}
          isEnabled={isEnabled("feargreed")}
          fearGreed={fearGreed}
          formatTime={formatTime}
        />

        {/* TcTrend badge overlay */}
        <TcTrendIndicator
          chartInstance={chartState.chartInstance}
          candleSeries={chartState.candleSeries}
          isEnabled={showTcTrend}
          trendSnapshot={tcTrend}
          trendChanges={tcTrendChanges}
          formatTime={formatTime}
          candles={candles}
        />

        {/* Breakout Signals */}
        <BreakoutSignalsIndicator
          chartInstance={chartState.chartInstance}
          candleSeries={chartState.candleSeries}
          isEnabled={showBreakouts}
          breakoutSignals={breakoutSignals}
          formatTime={formatTime}
        />
        <BreakoutSignalsIndicator
          chartInstance={chartState.chartInstance}
          candleSeries={chartState.candleSeries}
          isEnabled={showTcBreakout}
          breakoutSignals={tcBreakoutSignals}
          formatTime={formatTime}
          longColor="#22c55e"
          shortColor="#ef4444"
          longLabel="BUY"
          shortLabel="SELL"
        />
        <BreakoutSignalsIndicator
          chartInstance={chartState.chartInstance}
          candleSeries={chartState.candleSeries}
          isEnabled={showFearCarver}
          breakoutSignals={fearCarverSignals}
          formatTime={formatTime}
          longColor="#16a34a"
          shortColor="#dc2626"
          longLabel="BUY"
          shortLabel="SELL"
        />

        {/* Auction Market Theory Signals */}
        <AuctionMarketIndicator
          chartInstance={chartState.chartInstance}
          candleSeries={chartState.candleSeries}
          isEnabled={showAuctionMarket}
          auctionMarketEvents={auctionMarket}
          formatTime={formatTime}
          minConfidence={0.5}
          trendAlignedOnly={false}
        />

        {/* Auction Market Zones (VAH/VAL/POC area and lines) */}
        <AuctionMarketZones
          chartInstance={chartState.chartInstance}
          candleSeries={chartState.candleSeries}
          isEnabled={showAuctionMarket}
          auctionMarketEvents={auctionMarket}
          formatTime={formatTime}
        />

        {(showFearCarver && hoveredFearCarver) ||
        (showTcBreakout && hoveredTcBreakout) ? (
          <div className="absolute top-6 right-6 z-40 pointer-events-none flex flex-col gap-2">
            {showTcBreakout && hoveredTcBreakout && (
              <div className="rounded-lg px-4 py-3 shadow-xl border text-sm bg-slate-900/90 border-slate-600 text-slate-100">
                <div className="text-xs uppercase tracking-wide opacity-80">
                  TC Breakout
                </div>
                <div className="text-lg font-semibold">
                  {hoveredTcBreakout.action === "LONG" ? "BUY" : "SELL"}
                </div>
                <div className="text-xs mt-1">
                  Trend: {hoveredTcBreakout.trend ?? "-"}
                </div>
                <div className="text-[11px] opacity-80">
                  {new Date(hoveredTcBreakout.timestamp).toLocaleString()}
                </div>
              </div>
            )}
            {showFearCarver && hoveredFearCarver && (
              <div
                className={`rounded-lg px-4 py-3 shadow-xl border text-sm ${
                  hoveredFearCarver.action === "LONG"
                    ? "bg-emerald-900/90 border-emerald-400 text-emerald-100"
                    : "bg-rose-900/90 border-rose-400 text-rose-100"
                }`}
              >
                <div className="text-xs uppercase tracking-wide opacity-80">
                  Fear Carver Signal
                </div>
                <div className="text-lg font-semibold">
                  {hoveredFearCarver.action === "LONG" ? "BUY" : "SELL"}
                </div>
                <div className="text-xs mt-1">
                  Sentiment:{" "}
                  {hoveredFearCarver.sentiment !== undefined
                    ? `${hoveredFearCarver.sentiment.toFixed(0)}`
                    : "N/A"}
                </div>
                <div className="text-[11px] opacity-80">
                  {new Date(hoveredFearCarver.timestamp).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {!isMobileLayout && (
        <div className="hidden md:block px-4 py-4 bg-slate-900/90 border-t border-gray-800 backdrop-blur-sm relative z-[60] overflow-visible">
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={selectedPair}
              onChange={(e) => handlePairChange(e.target.value)}
              className="w-48 h-10 bg-slate-800 text-white border border-gray-600 rounded-md py-2 px-3"
              disabled={pairOptions.length === 0}
            >
              {pairOptions.length === 0 && (
                <option value="">No pairs available</option>
              )}
              {pairOptions.map((pair) => (
                <option key={pair} value={pair}>
                  {pair}
                </option>
              ))}
            </select>

            <select
              value={selectedTimeframe}
              onChange={(e) => handleTimeframeChange(e.target.value)}
              className="w-28 h-10 bg-slate-800 text-white border border-gray-600 rounded-md py-2 px-3"
              disabled={timeframeOptions.length === 0}
            >
              {timeframeOptions.length === 0 && (
                <option value="">No timeframes</option>
              )}
              {timeframeOptions.map((timeframe) => (
                <option key={timeframe} value={timeframe}>
                  {timeframe}
                </option>
              ))}
            </select>

            {showExchangeSelect && (
              <select
                value={selectedExchange}
                onChange={(e) => handleExchangeChange(e.target.value)}
                className="w-48 h-10 bg-slate-800 text-white border border-gray-600 rounded-md py-2 px-3"
                disabled={exchangeOptions.length === 0}
              >
                {exchangeOptions.length === 0 && (
                  <option value="">No exchanges available</option>
                )}
                {exchangeOptions.map((exchange) => (
                  <option key={exchange} value={exchange}>
                    {exchange}
                  </option>
                ))}
              </select>
            )}

            <IndicatorSelector
              onToggleIndicator={handleToggleIndicatorUI}
              enabledStates={enabledStates}
              allowedIndicatorIds={activeIndicatorIds}
              backtestableIndicatorIds={backtestableIndicatorIds}
              className="w-full md:w-[24rem]"
            />

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              <ActionButton
                onClick={handleBacktest}
                label="Backtest"
                variant="success"
                className="min-w-[140px]"
              />
            </div>
          </div>
        </div>
      )}

      <ChartFooter />

      <BacktestModal
        isOpen={isBacktestOpen}
        onClose={() => setBacktestOpen(false)}
        pair={selectedPair}
        exchange={selectedExchange}
        timeframe={selectedTimeframe}
        indicatorOptions={backtestIndicators}
        indicatorLoading={false}
        indicatorError={null}
      />

      <AuthRequiredModal
        isVisible={isAuthModalVisible}
        onClose={() => setAuthModalVisible(false)}
      />

      <AddTickerModal
        isVisible={isAddTickerVisible}
        onClose={() => toggleAddTickerModal(false)}
        onAdd={handleAddTicker}
        exchanges={exchanges}
        timeframes={timeframes?.map((t) => Timeframe[t as Timeframe])}
      />

      <EditTickerModal
        isVisible={isEditTickerVisible}
        onClose={() => toggleEditTickerModal(false)}
        onActivate={handleActivatePair}
        onDeactivate={handleDeactivatePair}
        tickers={allTickers}
        timeframes={timeframes?.map((t) => Timeframe[t as Timeframe])}
        exchanges={exchanges}
        currentPair={selectedPair}
      />

      <DebugModal
        isVisible={isDebugVisible}
        onClose={() => toggleDebugModal(false)}
        debugInfo={{
          selectedPair,
          selectedTimeframe,
          selectedExchange,
          candlesCount: candles.length,
          plasmaLinesCount: plasmaLines.length,
          supportCount: supLines.length,
          resistanceCount: resLines.length,
          vectorSignalsCount: vectorCandles.length,
          breakoutSignalsCount: breakoutSignals.length,
          enabledIndicators: getEnabled(),
          availableIndicators: activeIndicatorIds,
          socketStatus: socketRef.current?.connected
            ? "Connected"
            : "Disconnected",
          error,
        }}
      />

      {error && (
        <div className="fixed inset-x-0 bottom-6 flex justify-center pointer-events-none">
          <div className="bg-red-500 text-white px-4 py-2 rounded shadow-lg text-sm">
            {error}
          </div>
        </div>
      )}
    </div>
  );
};

export default RealtimeCandlestickChart;
